import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEnvironment } from "@keycloak/keycloak-account-ui";
import { Menu, User, ChevronDown, Lock, LogOut, Moon, Sun } from "lucide-react";
import { environment } from "../environment";
import { useTheme } from "../contexts/ThemeContext";

interface HeaderProps {
    onToggleMobileMenu: () => void;
}

export const Header = ({ onToggleMobileMenu }: HeaderProps) => {
    const { t } = useTranslation();
    const { keycloak } = useEnvironment();
    const { isDark, setTheme } = useTheme();
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleSignOut = () => {
        keycloak.logout({});
    };

    const handleThemeToggle = () => {
        setTheme(isDark ? "light" : "dark");
    };

    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
    const closeDropdown = () => setIsDropdownOpen(false);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                closeDropdown();
            }
        };

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <header className="flex-shrink-0 sticky top-0 z-30 border-b border-border-primary bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative flex items-center justify-between h-16">
                    <div className="flex items-center z-10">
                        {/* Mobile Menu Button */}
                        <button
                            onClick={onToggleMobileMenu}
                            className="p-2 rounded-md text-text-tertiary hover:text-text-secondary hover:bg-hover-medium mr-3 md:hidden"
                            aria-label="Open navigation menu"
                        >
                            <Menu className="w-6 h-6" />
                        </button>

                        <div className="hidden md:flex items-center lg:ml-2.5">
                            <img
                                src={`${environment.resourceUrl}/authhub-mark.svg`}
                                alt="AuthHub"
                                className="h-8 w-8 mr-3 object-contain"
                            />

                            <span className="text-2xl font-semibold tracking-tight text-text-primary">
                                AuthHub
                            </span>
                        </div>
                    </div>

                    <div className="z-10 flex items-center space-x-2 sm:space-x-4 lg:mr-[3.75rem] xl:mr-[5.5rem]">
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={toggleDropdown}
                                className="flex items-center p-2 text-text-tertiary hover:text-text-secondary focus:outline-none rounded-md"
                            >
                                <div className="w-8 h-8 bg-error-100 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-error-600" />
                                </div>
                                <ChevronDown
                                    className={`ml-1 w-4 h-4 transition-transform ${
                                        isDropdownOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </button>

                            {/* Dropdown Menu */}
                            {isDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-2xl border border-border-primary bg-surface shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                                    <div className="py-1" role="menu" aria-orientation="vertical">
                                        <button
                                            className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-4 px-5 py-3 text-left text-sm text-text-secondary transition-colors hover:bg-hover-medium"
                                            role="menuitem"
                                            onClick={() => {
                                                closeDropdown();
                                                navigate("personal-info");
                                            }}
                                        >
                                            <User className="mt-0.5 h-5 w-5 text-text-tertiary" />
                                            <span className="leading-snug">
                                                {t("personalInfo", "Personal info")}
                                            </span>
                                        </button>
                                        <button
                                            className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-4 px-5 py-3 text-left text-sm text-text-secondary transition-colors hover:bg-hover-medium"
                                            role="menuitem"
                                            onClick={() => {
                                                closeDropdown();
                                                navigate("security");
                                            }}
                                        >
                                            <Lock className="mt-0.5 h-5 w-5 text-text-tertiary" />
                                            <span className="leading-snug">
                                                {t("accountSecurity", "Security")}
                                            </span>
                                        </button>
                                        <button
                                            className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-4 px-5 py-3 text-left text-sm text-text-secondary transition-colors hover:bg-hover-medium"
                                            role="menuitem"
                                            onClick={() => {
                                                closeDropdown();
                                                handleThemeToggle();
                                            }}
                                        >
                                            {isDark ? (
                                                <Sun className="mt-0.5 h-5 w-5 text-text-tertiary" />
                                            ) : (
                                                <Moon className="mt-0.5 h-5 w-5 text-text-tertiary" />
                                            )}
                                            <span className="leading-snug">
                                                {isDark
                                                    ? t("theme.menu.light", "Light theme")
                                                    : t("theme.menu.dark", "Dark theme")}
                                            </span>
                                        </button>
                                        <hr className="my-1 border-border-primary" />
                                        <button
                                            className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-4 px-5 py-3 text-left text-sm text-error-600 transition-colors hover:bg-error-50"
                                            role="menuitem"
                                            onClick={() => {
                                                closeDropdown();
                                                handleSignOut();
                                            }}
                                        >
                                            <LogOut className="mt-0.5 h-5 w-5 text-error-500" />
                                            <span className="leading-snug">
                                                {t("signOut", "Sign out")}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};
